import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import Pagination from '@material-ui/lab/Pagination';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_LIVE_CLASSROOM_URL
} from 'config/urls';
import {
  ceil,
  chain,
  filter,
  find,
  get,
  isEmpty,
  isInteger,
  isNil,
  last,
  omit,
  some,
  sortBy
} from 'lodash';
import {
  getMultipleAssignmentsUrl,
  useAssignmentsNamesList,
  useTrackerInstanceSections,
  useUserSections,
  useUsersInUserSections,
  useYears
} from 'sdk';

import Filters from 'pages/Teachers/AssignmentsV2/components/Filters/';
import { downloadFile } from 'utils/files/';
import { bootIntercomWidget } from 'utils/integrations/intercom';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { parseParams, stringifyParams } from 'utils/urls';
import { reverse } from 'utils/urls';

import Assignment from './components/Assignment';
import AssignmentDialogContext from 'components/AssignmentDialogContext';
import Button from 'components/Button';
import ExportButton from 'components/ExportButton';
import UpdateGoogleClassroomRostersDialog from 'components/UpdateGoogleClassroomRostersDialog';
import UserContext from 'components/UserContext';

import { ReactComponent as PlusIcon } from './assets/add.svg';
import { ReactComponent as CaptureIcon } from './assets/capture.svg';
import { useNewSchoolYearDialog } from './hooks';
import styles from './styles.module.scss';

const DEFAULT_LIMIT = 10;

const AssignmentsV2 = () => {
  const { user } = useContext(UserContext);
  const assignmentDialogContext = useContext(AssignmentDialogContext);
  const history = useHistory();

  const {
    isUpdateGoogleClassroomRostersDialogOpened,
    closeUpdateGoogleClassroomRostersDialog,
    goToGoogleAuthScreen
  } = useNewSchoolYearDialog();

  const [filtersHaveBeenInitialized, setFiltersHaveBeenInitialized] =
    useState(false);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    numPages: 1,
    currentPage: 1,
    limit: DEFAULT_LIMIT
  });
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedAssignmentsIds, setSelectedAssignmentsIds] = useState(
    new Set()
  );
  const { setIsAssignmentDialogOpened } = useContext(AssignmentDialogContext);

  const author = get(filters, 'author');

  useEffect(() => {
    if (!user) {
      return;
    }
    bootIntercomWidget({
      email: user.email,
      name: user.name
    });
  }, [user]);

  const toggleAssignmentSelection = ({ id }) => {
    setSelectedAssignmentsIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const toggleMode = () => {
    if (isExportMode) {
      setSelectedAssignmentsIds(new Set());
    }

    setIsExportMode(!isExportMode);
  };

  const exportSelectedAssignments = async ({ selectedIds }) => {
    const url = await getMultipleAssignmentsUrl({
      tracker_instance_sections_ids: selectedIds
    });

    downloadFile({ url: url, filename: 'assignments.csv' });
    setSelectedAssignmentsIds(new Set());
    setIsExportMode(false);
  };

  const bulkExportAssignment = async () => {
    const idsToExport = Array.from(selectedAssignmentsIds);
    if (idsToExport.length === 0) return;

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'assignments',
        button: 'bulk_export',
        selected_count: idsToExport.length
      }
    );

    await exportSelectedAssignments({ selectedIds: idsToExport });
  };

  const trackersOverviewFilters = useMemo(() => {
    let offset = undefined;

    if (!isNil(pagination.limit)) {
      offset = pagination.limit * (pagination.currentPage - 1);
    }

    return omit({ ...filters, offset, section: filters.sectionName }, isNil);
  }, [filters, pagination]);

  const { data: sectionUsers, isLoading: isLoadingSectionUsers } =
    useUsersInUserSections();
  const { data: years, isLoading: isLoadingYears } = useYears();
  const { data: userSections, isLoading: isLoadingUserSections } =
    useUserSections({
      params: author ? { co_user: author } : {}
    });

  const assignmentsData = {
    ...(author && { co_user: author })
  };

  const {
    data: userAssignments,
    refetch: refetchAssignments,
    isLoading: isLoadingAssignments
  } = useAssignmentsNamesList({
    data: assignmentsData
  });

  const {
    data: trackersOverviewData,
    isLoading: isLoadingTrackersOverview,
    refetch: refetchTrackersOverview
  } = useTrackerInstanceSections({
    enabled: filtersHaveBeenInitialized,
    params: trackersOverviewFilters
  });

  const refetchTrackerOverviewCallback = useCallback(
    () => refetchTrackersOverview(),
    [refetchTrackersOverview]
  );

  useEffect(() => {
    if (assignmentDialogContext.isAssignmentDialogOpened) {
      assignmentDialogContext.setAssignAssignmentSuccessAction(
        () => refetchTrackerOverviewCallback
      );
    }
  }, [assignmentDialogContext, refetchTrackerOverviewCallback]);

  const loading = some([
    isLoadingSectionUsers,
    isLoadingYears,
    isLoadingUserSections,
    isLoadingAssignments,
    isLoadingTrackersOverview
  ]);

  const count = get(trackersOverviewData, 'count');
  const limit = get(trackersOverviewData, 'limit', DEFAULT_LIMIT);
  const trackerInstanceSectionData = get(trackersOverviewData, 'results');

  useEffect(() => {
    let numPages = 1;

    if (!isNil(count)) {
      numPages = ceil(count / limit);
    }

    setPagination((prevPagination) => ({
      ...prevPagination,
      numPages,
      limit
    }));
  }, [count, limit]);

  useEffect(() => {
    if (isLoadingYears || filtersHaveBeenInitialized) {
      return;
    }

    const filters = parseParams(history.location.search, {
      parseNumbers: true
    });
    let author = get(filters, 'author');

    // In case when author is completely missing or is not fully provided (e.g. `?author=`)
    if (!isInteger(author)) {
      const hasNonAuthorFilter = chain(filters)
        .omit('author')
        .values()
        .some((filterValue) => !isEmpty(filterValue))
        .value();

      // If any of assignments or sections filters are not empty => set author to null
      if (hasNonAuthorFilter) {
        author = null;
      }
    }

    const name = get(filters, 'name');
    const sectionName = get(filters, 'sectionName');

    const currentYear = last(sortBy(years, 'is_current'));
    const year = get(filters, 'year', get(currentYear, 'id'));

    setFiltersHaveBeenInitialized(true);
    setFilters((prevFilters) => ({
      ...prevFilters,
      author,
      name,
      sectionName,
      year
    }));
  }, [
    user.id,
    years,
    isLoadingYears,
    filtersHaveBeenInitialized,
    history.location.search
  ]);

  const sectionUsersForSearch = useMemo(() => {
    // Explicitly put `this.props.user` on top of the `sectionUsers` filter list
    const currentSectionUser = find(sectionUsers, { id: user.id });

    const sectionUsersWithoutCurrentUser = filter(
      sectionUsers,
      (u) => u.id !== user.id
    );

    return [currentSectionUser, ...sectionUsersWithoutCurrentUser];
  }, [sectionUsers, user.id]);

  const filterByAuthor = (e, user) =>
    setFilters((prevFilters) => ({
      ...prevFilters,
      author: get(user, 'id')
    }));

  const filterByYear = (year) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      year
    }));
  };
  const filterBySectionName = (e, section) =>
    setFilters((prevFilters) => ({
      ...prevFilters,
      sectionName: section
    }));

  const filterByAssignmentName = (e, name) =>
    setFilters((prevFilters) => ({
      ...prevFilters,
      name
    }));

  const changePageAndRefetchListData = (page) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: page
    }));
  };

  useEffect(() => {
    // Reset the pagination every time the filters change.
    changePageAndRefetchListData(1);
  }, [filters]);

  useEffect(() => {
    if (!loading) {
      history.replace({
        search: stringifyParams(filters)
      });
    }
  }, [filters, loading, history]);

  const selectedLabel = `Assignment${
    selectedAssignmentsIds.size !== 1 ? 's' : ''
  }`;
  const selectedUser = find(sectionUsers, { id: filters.author }) || null;
  const { numPages, currentPage } = pagination;

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.buttons}>
          {isExportMode && (
            <Button color="pink" onClick={bulkExportAssignment}>
              Export ({selectedAssignmentsIds.size}) {selectedLabel}
            </Button>
          )}

          <div className={styles.buttonContainer}>
            <ExportButton handleClick={toggleMode} />
          </div>
          <Button
            color="pink"
            startIcon={<PlusIcon />}
            onClick={() => {
              setIsAssignmentDialogOpened(true);
            }}
            data-intercom-target="Assignments page Create Assignment Button"
          >
            Create assignment
          </Button>

          <Button
            color="blue"
            startIcon={<CaptureIcon />}
            onClick={() => {
              history.push({
                pathname: reverse(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
              });
            }}
            data-intercom-target="Assignments page Capture Button"
          >
            Capture
          </Button>
        </div>
        <Filters
          user={user}
          filters={filters}
          selectedUser={selectedUser}
          authorOptions={sectionUsersForSearch}
          filterByAuthor={filterByAuthor}
          assignmentsOptions={userAssignments}
          filterByAssignmentName={filterByAssignmentName}
          sectionsOptions={userSections}
          filterBySectionName={filterBySectionName}
          yearsOptions={years}
          filterByYear={filterByYear}
        />
      </div>

      <div className={styles.header}>
        <div>Assignment name</div>
        <div>Section</div>
        <div>Due date</div>
        <div>Status</div>
        <div></div>
      </div>
      <div>
        {!loading &&
          trackerInstanceSectionData?.map((trackerInstanceSection) => (
            <Assignment
              key={trackerInstanceSection.id}
              name={trackerInstanceSection.tracker_name}
              sectionName={trackerInstanceSection.section_name}
              submittedWorks={trackerInstanceSection.submitted}
              dueAt={trackerInstanceSection.due_at}
              studentsCount={trackerInstanceSection.students_count}
              standards={trackerInstanceSection.standards}
              trackerId={trackerInstanceSection.tracker_id}
              assignmentId={trackerInstanceSection.id}
              isExportMode={isExportMode}
              isSelected={selectedAssignmentsIds.has(trackerInstanceSection.id)}
              onToggleSelect={() => {
                toggleAssignmentSelection({ id: trackerInstanceSection.id });
              }}
              onDeleteSuccess={() => {
                refetchTrackerOverviewCallback();
                refetchAssignments();
              }}
              onClick={() => {
                history.push({
                  pathname: reverse(
                    trackerInstanceSection.is_math_assignment
                      ? TEACHER_ASSIGNMENT_V2_LIVE_CLASSROOM_URL
                      : TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
                    {
                      trackerInstanceSectionId: trackerInstanceSection.id
                    }
                  ),
                  search: stringifyParams(filters)
                });
              }}
            />
          ))}
        {loading && (
          <div className={styles.loadingState}>
            <Skeleton variant="text" width={'100%'} height={200} />
            <Skeleton variant="text" width={'100%'} height={200} />
            <Skeleton variant="text" width={'100%'} height={200} />
          </div>
        )}
      </div>
      <div className={styles.pagination}>
        <Pagination
          count={numPages}
          page={currentPage}
          color="secondary"
          onChange={(event, page) => changePageAndRefetchListData(page)}
        />
      </div>
      {isUpdateGoogleClassroomRostersDialogOpened && (
        <UpdateGoogleClassroomRostersDialog
          onSync={goToGoogleAuthScreen}
          onClose={closeUpdateGoogleClassroomRostersDialog}
        />
      )}
    </>
  );
};

export default AssignmentsV2;
